import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { fetchReportData } from '../../../store/slices/reportSlice';
import { Divider } from '../../ui/Divider/Divider';
import { buildDeclarationData, buildFooterData, buildHeaderData, buildLegalBasisData } from './Report.utils';
import { LegalBasis } from './LegalBasis/LegalBasis';
import { ProviderDeclaration } from './ProviderDeclaration/ProviderDeclaration';
import { ReportFooter } from './ReportFooter/ReportFooter';
import { ReportHeader } from './ReportHeader/ReportHeader';
import { SystemInformation } from './SystemInformation/SystemInformation';

export function Report() {
  const dispatch = useDispatch<AppDispatch>();
  const { questionnaireId } = useSelector((state: RootState) => state.stepper);
  const {
    data: reportData,
    loading,
    error
  } = useSelector((state: RootState) => state.report);

  useEffect(() => {
    if (questionnaireId) {
      dispatch(fetchReportData(questionnaireId));
    }
  }, [dispatch, questionnaireId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Loading report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-500 text-lg">
          Error loading report: {error}
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">No report data available</div>
      </div>
    );
  }

  const headerData = buildHeaderData(reportData);
  const legalBasisData = buildLegalBasisData(reportData);
  const declarationData = buildDeclarationData(reportData);
  const footerData = buildFooterData(reportData);

  return (
    <div className="flex flex-col gap-5 px-6 md:px-12">
      <ReportHeader componentInfo={headerData} />
      <Divider classes="my-3.5" />
      {reportData.aiSystemInformation.description.trim() !== '' && (
        <>
          <SystemInformation
            title="AI System Information"
            description={reportData.aiSystemInformation.description}
          />
        </>
      )}
      <LegalBasis componentInfo={legalBasisData} />
      <Divider classes="my-3.5" />
      <ProviderDeclaration componentInfo={declarationData} />
      <Divider classes="my-3.5" />
      <ReportFooter componentInfo={footerData} />
    </div>
  );
}
